import React from 'react';

import { useState, MouseEvent } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import { makeStyles } from '@mui/styles';
import { i18next } from '@/config/i18n';

import { DownloadType } from './types';

const useStyles = makeStyles((theme: any) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    padding: '6px 12px',
    borderRadius: '4px',
    color: theme.palette.text.secondary,

    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  active: {
    backgroundColor: theme.palette.action.hover,
    color: `${theme.palette.primary.main} !important`,
    '& $caret': {
      transform: 'rotate(180deg)',
    },
  },
  icon: {
    color: 'inherit',
    fontSize: 22,
  },
  label: {
    color: 'inherit',
    fontSize: '0.9rem',
    marginLeft: theme.spacing(1),
    fontWeight: 500,
  },
  caret: {
    marginLeft: theme.spacing(0.5),
    fontSize: 18,
    transition: 'transform 0.2s ease',
    color: 'inherit',
  },
  menuItem: {
    padding: '8px 16px',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: `${theme.palette.action.hover} !important`,
      '& .MuiListItemIcon-root, & .MuiSvgIcon-root': {
        color: `${theme.palette.primary.main} !important`,
      },
    },
  },
  menuIcon: {
    minWidth: '36px !important',
    color: theme.palette.text.secondary,
    transition: 'color 0.2s ease',
  },
  menuText: {
    '& .MuiTypography-root': {
      fontSize: '0.9rem',
      fontWeight: 500,
    },
  },
}));

interface Props {
  onDownload: (type: DownloadType) => void;
}

export const DownloadButton = ({ onDownload }: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (type: DownloadType) => {
    handleClose();
    onDownload(type);
  };

  return (
    <>
      <Box 
        className={`${classes.container} ${open ? classes.active : ''}`} 
        onClick={handleOpen}
      >
        <DownloadIcon className={classes.icon} />
        <span className={classes.label}>
          {i18next.t('common:buttons.export')}
        </span>
        <KeyboardArrowDownIcon className={classes.caret} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        autoFocus={false}
        PaperProps={{
          elevation: 3,
          sx: {
            backgroundColor: 'background.paper',
            mt: 0.5,
            borderRadius: '8px',
            minWidth: '160px',
            '& .MuiList-root': {
              padding: '4px 0',
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem className={classes.menuItem} onClick={() => handleSelect('excel')}>
          <ListItemIcon className={classes.menuIcon}>
            <DescriptionOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText className={classes.menuText}>Excel</ListItemText>
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={() => handleSelect('pdf')}>
          <ListItemIcon className={classes.menuIcon}>
            <PictureAsPdfOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText className={classes.menuText}>PDF</ListItemText>
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={() => handleSelect('json')}>
          <ListItemIcon className={classes.menuIcon}>
            <CodeOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText className={classes.menuText}>JSON</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
